import { useState } from "react";
import {
  HelpCircle, Sparkles, Play, Loader2, Bot, BookOpen, BookOpenCheck,
  AlertTriangle, CheckCircle2, XCircle, Trophy,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

/**
 * Brief 3.4.11 — Unified knowledge preview ("What would Nerea say?").
 *
 * Replaces 3.4.9's PayTechFAQPreview. Routes through
 * `/admin/ops/knowledge-preview` so super_admins can see what BOTH
 * matchers return for an inbound merchant question + which one wins
 * (highest confidence; FAQ wins strict-equality tie-breaks per
 * founder spec) + the would-post text.
 *
 * Sources rendered:
 *   • faq                → PayTech FAQ matcher cleared its threshold
 *   • platform_knowledge → NeptunePay platform matcher cleared its threshold
 *   • brain              → both missed; fallthrough to brain
 *   • deflection         → out-of-scope, locked deflection copy
 *
 * Zero writes to live pipeline collections — one audit row per call.
 */
export default function KnowledgePreview({ merchantId }) {
  const { user } = useAuth();
  const [messageText, setMessageText] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  if (user?.role !== "super_admin") return null;

  const onRun = async () => {
    setError("");
    if (!messageText.trim()) { toast.error("Message text is required."); return; }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/ops/knowledge-preview", {
        merchant_id: merchantId,
        message_text: messageText,
      });
      setResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "object" ? JSON.stringify(detail) : (detail || "Preview failed."));
    } finally {
      setRunning(false);
    }
  };

  return (
    <section
      data-testid="knowledge-preview"
      className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6"
    >
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <HelpCircle className="h-4 w-4 text-cyan-300/80" />
          <h3 className="text-[15px] tracking-wide text-white">What would Nerea say?</h3>
          <span className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">
            · knowledge preview
          </span>
        </div>
        <span className="text-[11px] text-zinc-500">
          No writes · one audit row per run
        </span>
      </header>

      <div className="mt-5 space-y-3">
        <textarea
          data-testid="knowledge-preview-input"
          className="w-full rounded-md border border-white/[0.08] bg-black/30 px-3 py-2 text-[13px] text-white min-h-[88px]"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          placeholder="Hey, what does 'approved' mean for my application?"
        />
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onRun}
            disabled={running || !messageText.trim()}
            data-testid="knowledge-preview-run"
            className="inline-flex items-center gap-2 rounded-md border border-cyan-400/30 bg-cyan-400/[0.10] px-4 py-2 text-[12.5px] text-cyan-100 hover:bg-cyan-400/[0.18] disabled:opacity-40"
          >
            {running
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Play className="h-3.5 w-3.5" />}
            Run preview
          </button>
          {result && (
            <button
              type="button"
              onClick={() => { setResult(null); setMessageText(""); }}
              data-testid="knowledge-preview-reset"
              className="inline-flex items-center gap-2 rounded-md border border-white/[0.10] px-3 py-2 text-[12px] text-zinc-400 hover:text-white"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {error && (
        <div
          data-testid="knowledge-preview-error"
          className="mt-4 rounded-xl border border-rose-400/20 bg-rose-400/[0.03] p-3 text-rose-200 text-[12.5px]"
        >
          {error}
        </div>
      )}

      {result && <PreviewResult result={result} />}
    </section>
  );
}

const SOURCE_META = {
  faq: {
    icon: BookOpen, label: "FAQ match",
    tone: "border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-100",
  },
  platform_knowledge: {
    icon: BookOpenCheck, label: "Platform knowledge match",
    tone: "border-violet-400/30 bg-violet-400/[0.06] text-violet-100",
  },
  brain: {
    icon: Bot, label: "Brain (fallthrough)",
    tone: "border-cyan-400/30 bg-cyan-400/[0.06] text-cyan-100",
  },
  deflection: {
    icon: AlertTriangle, label: "Out-of-scope deflection",
    tone: "border-amber-400/30 bg-amber-400/[0.06] text-amber-100",
  },
};

function PreviewResult({ result }) {
  const meta = SOURCE_META[result.source] || SOURCE_META.brain;
  const Icon = meta.icon;
  return (
    <div data-testid="knowledge-preview-result" className="mt-5 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11.5px] ${meta.tone}`}>
          <Icon className="h-3 w-3" /> {meta.label}
        </span>
        {result.dual_high_confidence && (
          <span data-testid="knowledge-preview-dual-flag" className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/[0.08] px-2.5 py-0.5 text-[10.5px] text-amber-200 font-mono">
            <AlertTriangle className="h-2.5 w-2.5" /> dual ≥ 0.80 — tie-break engaged
          </span>
        )}
        <span className="text-[11px] text-zinc-500 font-mono">
          {result.request_id}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[12.5px]">
        <KeyValue
          k="Scope"
          v={
            <span className="font-mono text-[12px]">
              {result.scope?.in_scope ? <CheckCircle2 className="inline h-3.5 w-3.5 text-emerald-300 mr-1" /> : <XCircle className="inline h-3.5 w-3.5 text-zinc-600 mr-1" />}
              {result.scope?.category || "unknown"}
              {typeof result.scope?.confidence === "number" ? ` · ${result.scope.confidence.toFixed(2)}` : ""}
            </span>
          }
        />
        <MatchSummary
          label="PayTech FAQ"
          match={result.faq_match}
          idKey="faq_id"
          isWinner={result.source === "faq"}
          testid="knowledge-preview-faq-match"
        />
        <MatchSummary
          label="Platform knowledge"
          match={result.platform_match}
          idKey="npk_id"
          isWinner={result.source === "platform_knowledge"}
          testid="knowledge-preview-platform-match"
        />
      </div>

      {result.source === "faq" && result.faq_match?.source_entry && (
        <SourceEntryCard
          entry={result.faq_match.source_entry}
          tone="emerald"
          testid="knowledge-preview-source-entry"
        />
      )}
      {result.source === "platform_knowledge" && result.platform_match?.source_entry && (
        <SourceEntryCard
          entry={result.platform_match.source_entry}
          tone="violet"
          testid="knowledge-preview-source-entry"
        />
      )}

      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">
          <Sparkles className="inline h-3 w-3 mr-1 -translate-y-px text-cyan-300/70" />
          Would post
        </div>
        <div
          data-testid="knowledge-preview-would-post"
          className="rounded-md border border-white/[0.08] bg-black/30 p-3 text-[13px] text-zinc-100 whitespace-pre-wrap break-words"
        >
          {result.would_post || <span className="text-zinc-500 italic">(empty)</span>}
        </div>
      </div>

      {result.brain_output && result.source === "brain" && (
        <details>
          <summary className="text-[11px] text-zinc-500 cursor-pointer hover:text-white">
            Brain telemetry
          </summary>
          <pre className="mt-2 rounded-md border border-white/[0.05] bg-black/30 p-2.5 text-[11px] text-zinc-400 overflow-x-auto">
{JSON.stringify({ confidence_qual: result.brain_output.confidence_qual, abstained: result.brain_output.abstained, redacted: result.brain_output.redacted, error: result.brain_output.error }, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function MatchSummary({ label, match, idKey, isWinner, testid }) {
  if (!match) {
    return (
      <KeyValue
        k={label}
        v={<span className="text-zinc-500 italic">not evaluated</span>}
      />
    );
  }
  const id = match[idKey];
  const conf = typeof match.confidence === "number" ? match.confidence : 0;
  const thr = typeof match.threshold === "number" ? match.threshold : 0.8;
  return (
    <div data-testid={testid} className={`rounded-md border ${isWinner ? "border-emerald-400/40 bg-emerald-400/[0.04]" : "border-white/[0.06] bg-black/20"} p-2.5`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">{label}</div>
        {isWinner && <Trophy className="h-3 w-3 text-emerald-300" />}
      </div>
      {id ? (
        <div className="mt-0.5 font-mono text-[12px]">
          {id}
          {" · "}
          <span className={conf >= thr ? "text-emerald-200" : "text-amber-200"}>
            {conf.toFixed(2)}
          </span>
          <span className="text-zinc-500"> / {thr.toFixed(2)}</span>
        </div>
      ) : (
        <div className="mt-0.5 text-zinc-500 text-[11.5px] italic">
          no match{match.reasoning ? ` · ${match.reasoning}` : ""}
        </div>
      )}
    </div>
  );
}

function SourceEntryCard({ entry, tone, testid }) {
  const tones = {
    emerald: "border-emerald-400/20 bg-emerald-400/[0.025] text-emerald-200/80",
    violet: "border-violet-400/20 bg-violet-400/[0.025] text-violet-200/80",
  };
  return (
    <div data-testid={testid} className={`rounded-md border ${tones[tone]} p-3 text-[12.5px]`}>
      <div className="text-[10.5px] uppercase tracking-[0.22em] mb-1">
        Source entry · {entry.topic} · v{entry.version}
      </div>
      <div className="text-zinc-200 italic">Q: {entry.question}</div>
    </div>
  );
}

function KeyValue({ k, v }) {
  return (
    <div className="rounded-md border border-white/[0.06] bg-black/20 p-2.5">
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">{k}</div>
      <div className="mt-0.5 text-zinc-200">{v ?? <span className="text-zinc-600">—</span>}</div>
    </div>
  );
}
